import type { DevicePlatform } from "../web-push";

/** Что известно об устройстве и странице, чтобы понять, почему нельзя установить. */
export interface InstallEnvironment {
  /** Страница открыта по https:// или на localhost. */
  secureContext: boolean;
  /** Браузер умеет service worker. */
  serviceWorkerSupported: boolean;
  /** Service worker приложения запущен (в dev-режиме и при сбое регистрации — нет). */
  workerRegistered: boolean;
  platform: DevicePlatform | null;
  userAgent: string;
}

export interface InstallBlocker {
  /** Что мешает. */
  problem: string;
  /** Что сделать. */
  fix: string;
}

/**
 * Причины, по которым браузер не предлагает установить приложение, и что с
 * ними делать. Возвращает хотя бы одну: если явных причин нет, объясняет, что
 * решение принимает сам браузер.
 */
export function installBlockers(env: InstallEnvironment): InstallBlocker[] {
  const blockers: InstallBlocker[] = [];
  const firefox = /firefox|fxios/i.test(env.userAgent);
  const desktop = env.platform !== "android" && env.platform !== "ios";

  if (!env.secureContext) {
    blockers.push({
      problem:
        "Страница открыта по незащищённому адресу (http://). Браузеры разрешают установку и фоновую службу приложения только по https:// и на localhost.",
      fix: "Откройте ERP по адресу с https:// — его можно уточнить у администратора. Для локальной проверки в Chrome, Edge и Яндекс Браузере можно добавить адрес в chrome://flags/#unsafely-treat-insecure-origin-as-secure. Пока этого нет, создайте ярлык по инструкции ниже.",
    });
  }
  if (env.platform === "ios") {
    blockers.push({
      problem:
        "Safari на iPhone и iPad не показывает окно установки: приложение добавляется вручную.",
      fix: "Откройте ERP в Safari, нажмите «Поделиться» и выберите «На экран «Домой»». Подробности — в инструкции «iPhone и iPad» ниже.",
    });
  }
  if (firefox && desktop) {
    blockers.push({
      problem: "Firefox на компьютере не умеет устанавливать веб-приложения.",
      fix: "Откройте ERP в Chrome, Edge или Яндекс Браузере и установите её оттуда.",
    });
  }
  if (env.secureContext && !env.serviceWorkerSupported) {
    blockers.push({
      problem:
        "Этот браузер не поддерживает фоновые службы (service worker), без которых установка невозможна.",
      fix: "Обновите браузер или откройте ERP в современном Chrome, Edge, Яндекс Браузере или Safari.",
    });
  }
  if (
    env.secureContext &&
    env.serviceWorkerSupported &&
    !env.workerRegistered
  ) {
    blockers.push({
      problem:
        "Фоновая служба приложения не запущена: это бывает в режиме разработки или если регистрация не удалась.",
      fix: "Откройте собранную версию ERP (не dev-сервер) и обновите страницу. Если не помогло — очистите данные сайта в настройках браузера и откройте ERP заново.",
    });
  }
  if (blockers.length === 0) {
    blockers.push({
      problem:
        "Браузер сам решает, когда предложить установку: обычно это происходит через несколько секунд после открытия страницы. Он также молчит, если приложение уже установлено на этом устройстве или вы недавно отклонили предложение.",
      fix: "Проверьте, нет ли ERP среди установленных приложений (меню «Пуск», список приложений, экран «Домой»). Иначе обновите страницу и подождите несколько секунд либо установите вручную через меню браузера — инструкция ниже.",
    });
  }

  return blockers;
}
